import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { videos, getFeaturedVideos, getHotVideos, getLatestVideos } from '@/mocks/videos';
import { getLatestArticles } from '@/mocks/articles';

export default function Home() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const featuredVideos = getFeaturedVideos();
  const hotVideos = getHotVideos();
  const latestVideos = getLatestVideos(8);
  const latestArticles = getLatestArticles(5);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('订阅成功！感谢您的关注。');
      form.reset();
      return;
    }
    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9n1igu8mbnljin59sr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('订阅成功！感谢您的关注。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '51视频 - 首页',
    description: '51视频提供51视频在线、91在线视频等高清免费影视内容，播放流畅不卡顿，免费在线观看电影电视剧全集，不缓冲。',
    url: 'https://www.51video.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '51视频',
      url: 'https://www.51video.com/',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: featuredVideos.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Movie',
          name: v.title,
          url: `https://www.51video.com/article/${v.id}`,
          image: v.thumbnail,
          description: v.description,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20movie%20theater%20atmosphere%20with%20warm%20golden%20spotlight%20beams%2C%20abstract%20film%20strip%20patterns%2C%20luxurious%20velvet%20texture%2C%20dramatic%20shadow%20play%2C%20entertainment%20industry%20elegance%2C%20rich%20dark%20tones%20with%20coral%20red%20accents%2C%20atmospheric%20lighting&width=1920&height=900&seq=hero-bg-51video&orientation=landscape"
              alt="51视频 - 高清免费在线观看"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-950"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-50 font-heading mb-4 leading-tight">
                51视频在线高清免费观看
                <br />
                <span className="text-primary-400">不卡顿·不缓冲</span>
              </h1>
              <p className="text-foreground-300 text-sm md:text-lg max-w-2xl mb-8 leading-relaxed">
                51视频提供51视频在线、91在线视频等高清免费影视内容，播放流畅不卡顿，
                免费在线观看电影电视剧全集，不缓冲。涵盖热门电影、电视剧、综艺、动漫等多种类型。
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/category/dianying"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-full transition-all whitespace-nowrap text-sm"
                >
                  <i className="ri-play-fill mr-1"></i>立即观看
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 border border-foreground-400/30 text-foreground-200 hover:bg-foreground-50/10 rounded-full transition-all whitespace-nowrap text-sm"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Category Grid */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading">
                    视频分类
                  </h2>
                  <p className="text-foreground-500 text-sm mt-1">选择你感兴趣的视频类型</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-900 hover:bg-background-800 border border-background-800/50 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400 group-hover:bg-primary-500 group-hover:text-background-950 transition-all duration-300">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <span className="text-foreground-200 text-sm font-medium whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Videos */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-900/50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading">
                    精选推荐
                  </h2>
                  <p className="text-foreground-500 text-sm mt-1">编辑精选的高品质影视内容</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {featuredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} size="large" />
                ))}
              </div>
            </div>
          </section>

          {/* Hot Videos */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading">
                    热门推荐
                  </h2>
                  <p className="text-foreground-500 text-sm mt-1">大家都在看的热门内容</p>
                </div>
                <Link
                  to="/category/dianying"
                  className="text-primary-400 hover:text-primary-300 text-sm transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  查看更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {hotVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-900/50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {[
                  { value: '10万+', label: '影视资源', icon: 'ri-movie-2-line' },
                  { value: '5000万+', label: '活跃用户', icon: 'ri-user-heart-line' },
                  { value: '99.9%', label: '播放成功率', icon: 'ri-check-double-line' },
                  { value: '4K', label: '超清画质', icon: 'ri-4k-line' },
                ].map((stat, i) => (
                  <div key={i} className="p-6 rounded-xl bg-background-900 border border-background-800/50">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-accent-500 font-heading">{stat.value}</div>
                    <div className="text-foreground-500 text-sm mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading">
                    最新更新
                  </h2>
                  <p className="text-foreground-500 text-sm mt-1">最近更新的影视内容</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {latestVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>

          {/* Articles & Newsletter */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-900/50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Latest Articles */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-6">
                    最新资讯
                  </h2>
                  <div className="space-y-4">
                    {latestArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="flex gap-4 p-4 rounded-xl bg-background-900 hover:bg-background-800 border border-background-800/50 hover:border-primary-500/20 transition-all group cursor-pointer"
                      >
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-foreground-100 text-sm font-medium line-clamp-2 group-hover:text-primary-400 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-foreground-500 text-xs mt-1 line-clamp-2">{article.excerpt}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-600">
                            <span>{article.publishDate}</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i> {article.views}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Newsletter */}
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading mb-6">
                    订阅更新
                  </h2>
                  <div className="p-6 md:p-8 rounded-xl bg-background-900 border border-background-800/50">
                    <p className="text-foreground-400 text-sm mb-6 leading-relaxed">
                      订阅51视频更新通知，第一时间获取最新影视资源和观影推荐。高清免费在线观看，播放流畅不缓冲。
                    </p>
                    <form onSubmit={handleSubscribe} data-readdy-form className="space-y-4">
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="请输入您的邮箱地址"
                        className="w-full px-4 py-3 bg-background-800 border border-background-700 rounded-lg text-sm text-foreground-200 placeholder-foreground-600 outline-none focus:border-primary-500 transition-colors"
                      />
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="form-antispam-field"
                      />
                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold rounded-lg transition-all whitespace-nowrap text-sm"
                      >
                        立即订阅
                      </button>
                      {formStatus !== 'idle' && (
                        <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-500' : 'text-red-400'}`}>
                          {formMessage}
                        </p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading text-center mb-8">
                常见问题
              </h2>
              <script type="application/ld+json">
                {JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'FAQPage',
                  mainEntity: [
                    {
                      '@type': 'Question',
                      name: '51视频需要付费吗？',
                      acceptedAnswer: { '@type': 'Answer', text: '51视频所有内容均免费在线观看，无需付费。我们提供高清流畅的播放体验，不卡顿不缓冲。' },
                    },
                    {
                      '@type': 'Question',
                      name: '51视频支持哪些设备观看？',
                      acceptedAnswer: { '@type': 'Answer', text: '51视频支持电脑、平板、手机等主流设备，只要连接网络即可随时随地在线观看。' },
                    },
                    {
                      '@type': 'Question',
                      name: '视频播放卡顿怎么办？',
                      acceptedAnswer: { '@type': 'Answer', text: '51视频采用先进的视频传输技术，一般情况下不会出现卡顿。如遇卡顿，建议检查网络连接或切换清晰度。' },
                    },
                  ],
                })}
              </script>
              <div className="space-y-3">
                {[
                  { q: '51视频需要付费吗？', a: '51视频所有内容均免费在线观看，无需付费。我们提供高清流畅的播放体验，不卡顿不缓冲。' },
                  { q: '51视频支持哪些设备观看？', a: '51视频支持电脑、平板、手机等主流设备，只要连接网络即可随时随地在线观看。' },
                  { q: '视频播放卡顿怎么办？', a: '51视频采用先进的视频传输技术，一般情况下不会出现卡顿。如遇卡顿，建议检查网络连接或切换清晰度。' },
                  { q: '如何搜索想看的视频？', a: '您可以通过顶部的搜索框输入关键词搜索，也可以按分类浏览。51视频提供电影、电视剧、综艺、动漫等多种分类。' },
                ].map((faq, i) => (
                  <details key={i} className="group rounded-xl bg-background-900 border border-background-800/50">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-foreground-200 text-sm font-medium">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <p className="px-5 pb-4 text-foreground-500 text-sm leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}